(() => {
  "use strict";

  const SELECTORS = {
    section: "[data-cvh]",
    video: ".cvh__video",
    text: "[data-cvh-text]",
    line: "[data-cvh-line]"
  };

  const CLASSES = {
    active: "cvh__text--active",
    leaving: "cvh__text--leaving",
    lineAnimate: "cvh__line--animate"
  };

  const LEAVE_DURATION = 620;
  const LINE_DURATION = 720;

  class CinematicVideoHero {
    constructor(section) {
      this.section = section;
      this.video = section.querySelector(SELECTORS.video);
      this.texts = Array.from(
        section.querySelectorAll(SELECTORS.text)
      );
      this.line = section.querySelector(SELECTORS.line);

      this.activeIndex = -1;
      this.leaveTimer = null;
      this.lineTimer = null;

      this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
      this.handleVideoReset = this.handleVideoReset.bind(this);

      if (!this.video || this.texts.length === 0) {
        return;
      }

      this.bindEvents();
      this.updateActiveText();
    }

    bindEvents() {
      this.video.addEventListener(
        "timeupdate",
        this.handleTimeUpdate
      );

      this.video.addEventListener(
        "loadedmetadata",
        this.handleTimeUpdate
      );

      this.video.addEventListener(
        "seeked",
        this.handleTimeUpdate
      );

      this.video.addEventListener(
        "play",
        this.handleTimeUpdate
      );

      this.video.addEventListener(
        "ended",
        this.handleVideoReset
      );
    }

    handleTimeUpdate() {
      this.updateActiveText();
    }

    handleVideoReset() {
      this.activateText(0);
    }

    getActiveIndex(currentTime) {
      return this.texts.findIndex((text) => {
        const start = Number.parseFloat(text.dataset.start);
        const end = Number.parseFloat(text.dataset.end);

        return (
          Number.isFinite(start) &&
          Number.isFinite(end) &&
          currentTime >= start &&
          currentTime < end
        );
      });
    }

    updateActiveText() {
      const nextIndex = this.getActiveIndex(
        this.video.currentTime
      );

      if (nextIndex !== -1) {
        this.activateText(nextIndex);
      }
    }

    activateText(nextIndex) {
      if (nextIndex === this.activeIndex) {
        return;
      }

      window.clearTimeout(this.leaveTimer);

      const currentText =
        this.activeIndex >= 0
          ? this.texts[this.activeIndex]
          : null;

      const nextText = this.texts[nextIndex];

      if (!nextText) {
        return;
      }

      if (currentText) {
        currentText.classList.remove(CLASSES.active);
        currentText.classList.add(CLASSES.leaving);

        this.leaveTimer = window.setTimeout(() => {
          currentText.classList.remove(CLASSES.leaving);
        }, LEAVE_DURATION);
      }

      nextText.classList.remove(CLASSES.leaving);

      window.requestAnimationFrame(() => {
        nextText.classList.add(CLASSES.active);
      });

      this.animateLine();
      this.activeIndex = nextIndex;
    }

    animateLine() {
      if (!this.line) {
        return;
      }

      window.clearTimeout(this.lineTimer);

      this.line.classList.remove(CLASSES.lineAnimate);

      void this.line.offsetWidth;

      this.line.classList.add(CLASSES.lineAnimate);

      this.lineTimer = window.setTimeout(() => {
        this.line.classList.remove(
          CLASSES.lineAnimate
        );
      }, LINE_DURATION);
    }

    destroy() {
      window.clearTimeout(this.leaveTimer);
      window.clearTimeout(this.lineTimer);

      this.video.removeEventListener(
        "timeupdate",
        this.handleTimeUpdate
      );

      this.video.removeEventListener(
        "loadedmetadata",
        this.handleTimeUpdate
      );

      this.video.removeEventListener(
        "seeked",
        this.handleTimeUpdate
      );

      this.video.removeEventListener(
        "play",
        this.handleTimeUpdate
      );

      this.video.removeEventListener(
        "ended",
        this.handleVideoReset
      );
    }
  }

  const instances = new Map();

  function initializeSection(section) {
    if (!section || instances.has(section)) {
      return;
    }

    instances.set(
      section,
      new CinematicVideoHero(section)
    );
  }

  function initializeAll() {
    document
      .querySelectorAll(SELECTORS.section)
      .forEach(initializeSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initializeAll
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      const section = event.target.matches(
        SELECTORS.section
      )
        ? event.target
        : event.target.querySelector(
            SELECTORS.section
          );

      if (section) {
        initializeSection(section);
      }
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      const section = event.target.matches(
        SELECTORS.section
      )
        ? event.target
        : event.target.querySelector(
            SELECTORS.section
          );

      const instance = instances.get(section);

      if (instance) {
        instance.destroy();
        instances.delete(section);
      }
    }
  );
})();